import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { graphqlRequest } from '../utils/graphqlRequest';
import '../styles/AdminClockWidget.css';

const CLOCK_OPEN = '[CLOCK_OPEN]';

function localDateYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Backend marks an open shift with timeOut === timeIn until clock-out. */
function isOpenTimeCard(card) {
  if (!card) return false;
  if (String(card.notes || '').includes(CLOCK_OPEN)) return true;
  const timeIn = String(card.timeIn || '').trim();
  const timeOut = String(card.timeOut || '').trim();
  return Boolean(timeIn && timeOut && timeIn === timeOut);
}

const FIND_USER_CARDS = `
  query FindUserTimeCards($userId: String!, $date: String!) {
    findTimeCards(userId: $userId, date: $date, omitMedia: true) {
      id
      date
      timeIn
      timeOut
      duration
      notes
      approvalStatus
    }
  }
`;

const MUT_CLOCK_IN = `
  mutation AdminClockIn {
    adminClockIn {
      id
      timeIn
      timeOut
      notes
      duration
    }
  }
`;

const MUT_CLOCK_OUT = `
  mutation AdminClockOut {
    adminClockOut {
      id
      timeIn
      timeOut
      notes
      duration
    }
  }
`;

export default function AdminClockWidget() {
  const userId = useMemo(() => {
    const fromCookie = Cookies.get('userId');
    if (fromCookie) return String(fromCookie);
    const t = Cookies.get('accessToken');
    if (!t) return '';
    try {
      return String(jwtDecode(t)?.sub?.userId || '');
    } catch {
      return '';
    }
  }, []);

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const today = localDateYmd();
      const data = await graphqlRequest(FIND_USER_CARDS, { userId, date: today });
      const list = Array.isArray(data?.findTimeCards) ? data.findTimeCards : [];
      setCards(list);
    } catch (e) {
      setError(e?.message || 'Could not load today’s time cards.');
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openEntry = useMemo(() => cards.find(isOpenTimeCard), [cards]);

  const clockedIn = Boolean(openEntry);

  const onClockIn = async () => {
    setBusy(true);
    setMessage('');
    setError('');
    try {
      await graphqlRequest(MUT_CLOCK_IN);
      setMessage('Clocked in.');
      await refresh();
    } catch (e) {
      setError(e?.message || 'Clock in failed.');
    } finally {
      setBusy(false);
    }
  };

  const onClockOut = async () => {
    setBusy(true);
    setMessage('');
    setError('');
    try {
      await graphqlRequest(MUT_CLOCK_OUT);
      setMessage('Clocked out.');
      await refresh();
    } catch (e) {
      setError(e?.message || 'Clock out failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!userId) return null;

  return (
    <div className="admin-clock-widget">
      <div className="admin-clock-widget__head">
        <h3>Time clock</h3>
        <span
          className={
            clockedIn
              ? 'admin-clock-widget__status admin-clock-widget__status--in'
              : 'admin-clock-widget__status admin-clock-widget__status--out'
          }
        >
          {clockedIn ? `In since ${openEntry?.timeIn || '—'}` : 'Not clocked in'}
        </span>
      </div>
      {loading ? <p className="admin-clock-widget__hint">Loading…</p> : null}
      {message ? <p className="admin-clock-widget__ok">{message}</p> : null}
      {error ? <p className="admin-clock-widget__err">{error}</p> : null}
      <div className="admin-clock-widget__actions">
        <button
          type="button"
          className="admin-clock-widget__btn admin-clock-widget__btn--in"
          disabled={busy || clockedIn}
          onClick={onClockIn}
        >
          Clock in
        </button>
        <button
          type="button"
          className="admin-clock-widget__btn admin-clock-widget__btn--out"
          disabled={busy || !clockedIn}
          onClick={onClockOut}
        >
          Clock out
        </button>
      </div>
    </div>
  );
}
